import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardHeader, Button, Input, Textarea } from '@/components/ui';
import {
  Save,
  Building2,
  DollarSign,
  Share2,
  Smartphone,
  Search,
  Loader2,
  Check,
  AlertCircle,
} from 'lucide-react';
import { clsx } from 'clsx';

interface Settings {
  // Theater Info
  theaterName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  partyPhone: string;
  email: string;

  // Ticket Prices
  priceMatinee: string;
  priceEvening: string;
  priceDiscount: string;
  price3dAddon: string;

  // Social Links
  facebook: string;
  instagram: string;
  twitter: string;

  // App Links
  iosAppUrl: string;
  androidAppUrl: string;

  // SEO
  metaTitle: string;
  metaDescription: string;

  // RTS Integration
  rtsEnabled: boolean;
  rtsTheaterCode: string;
  rtsApiKey: string;
}

type SettingsSection = 'theater' | 'prices' | 'social' | 'apps' | 'seo' | 'rts';

const sections: { id: SettingsSection; label: string; icon: React.ReactNode }[] = [
  { id: 'theater', label: 'Theater Info', icon: <Building2 className="h-4 w-4" /> },
  { id: 'prices', label: 'Ticket Prices', icon: <DollarSign className="h-4 w-4" /> },
  { id: 'social', label: 'Social Links', icon: <Share2 className="h-4 w-4" /> },
  { id: 'apps', label: 'Mobile Apps', icon: <Smartphone className="h-4 w-4" /> },
  { id: 'seo', label: 'SEO', icon: <Search className="h-4 w-4" /> },
];

const defaultSettings: Settings = {
  theaterName: 'DMAX Cinema',
  address: '4319 N. I-35',
  city: 'Gainesville',
  state: 'TX',
  zipCode: '76240',
  phone: '940.665.9999',
  partyPhone: '903.491.8118',
  email: 'dmaxtheaters@gmail.com',
  priceMatinee: '7.50',
  priceEvening: '9.75',
  priceDiscount: '7.50',
  price3dAddon: '3.25',
  facebook: '',
  instagram: '',
  twitter: '',
  iosAppUrl: '',
  androidAppUrl: '',
  metaTitle: 'DMAX Cinema | Gainesville, TX Movie Theater',
  metaDescription: 'Experience movies at DMAX Cinema in Gainesville, TX. 8 screens, all digital, with affordable prices and the best popcorn in town.',
  rtsEnabled: false,
  rtsTheaterCode: '',
  rtsApiKey: '',
};

export function SettingsPage() {
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState<SettingsSection>('theater');
  const [formData, setFormData] = useState<Settings>(defaultSettings);
  const [hasChanges, setHasChanges] = useState(false);

  const { data: settings, isLoading } = useQuery<Settings>({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await api.get('/admin/settings');
      return response.data?.data;
    },
  });

  useEffect(() => {
    if (settings) {
      setFormData({ ...defaultSettings, ...settings });
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async (data: Settings) => {
      return api.put('/admin/settings', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setHasChanges(false);
    },
  });

  function handleChange(field: keyof Settings, value: string | boolean) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  }

  function handleSave() {
    saveMutation.mutate(formData);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-gray-400 mt-1">Manage theater configuration</p>
        </div>
        <Button onClick={handleSave} disabled={!hasChanges} loading={saveMutation.isPending}>
          <Save className="h-4 w-4 mr-2" />
          Save Changes
        </Button>
      </div>

      {/* Success/Error Messages */}
      {saveMutation.isSuccess && (
        <div className="flex items-center gap-2 rounded-lg bg-green-500/10 border border-green-500/20 px-4 py-3 text-sm text-green-400">
          <Check className="h-4 w-4" />
          Settings saved successfully
        </div>
      )}

      {saveMutation.isError && (
        <div className="flex items-center gap-2 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
          <AlertCircle className="h-4 w-4" />
          Failed to save settings
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <Card padding="md" className="lg:col-span-1 h-fit">
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors',
                  activeSection === section.id
                    ? 'bg-gold/10 text-gold'
                    : 'text-gray-400 hover:bg-midnight-100 hover:text-white'
                )}
              >
                {section.icon}
                <span className="font-medium">{section.label}</span>
              </button>
            ))}
          </nav>
        </Card>

        {/* Content */}
        <div className="lg:col-span-3">
          {activeSection === 'theater' && (
            <Card padding="lg">
              <CardHeader title="Theater Information" description="Basic theater details" />
              <div className="space-y-4">
                <Input
                  label="Theater Name"
                  value={formData.theaterName}
                  onChange={(e) => handleChange('theaterName', e.target.value)}
                />
                <Input
                  label="Address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                />
                <div className="grid grid-cols-3 gap-4">
                  <Input
                    label="City"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                  />
                  <Input
                    label="State"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                  />
                  <Input
                    label="ZIP Code"
                    value={formData.zipCode}
                    onChange={(e) => handleChange('zipCode', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Main Phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                  <Input
                    label="Party Zone Phone"
                    value={formData.partyPhone}
                    onChange={(e) => handleChange('partyPhone', e.target.value)}
                  />
                </div>
                <Input
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
              </div>
            </Card>
          )}

          {activeSection === 'prices' && (
            <Card padding="lg">
              <CardHeader title="Ticket Prices" description="Configure ticket pricing" />
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Matinee Price"
                    value={formData.priceMatinee}
                    onChange={(e) => handleChange('priceMatinee', e.target.value)}
                    helper="Before 6pm"
                  />
                  <Input
                    label="Evening Price"
                    value={formData.priceEvening}
                    onChange={(e) => handleChange('priceEvening', e.target.value)}
                    helper="After 6pm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Discount Price"
                    value={formData.priceDiscount}
                    onChange={(e) => handleChange('priceDiscount', e.target.value)}
                    helper="Senior, Child, Military"
                  />
                  <Input
                    label="3D Add-on"
                    value={formData.price3dAddon}
                    onChange={(e) => handleChange('price3dAddon', e.target.value)}
                    helper="Additional charge for 3D"
                  />
                </div>
              </div>
            </Card>
          )}

          {activeSection === 'social' && (
            <Card padding="lg">
              <CardHeader title="Social Media" description="Links to your social profiles" />
              <div className="space-y-4">
                <Input
                  label="Facebook"
                  value={formData.facebook}
                  onChange={(e) => handleChange('facebook', e.target.value)}
                  placeholder="https://facebook.com/..."
                />
                <Input
                  label="Instagram"
                  value={formData.instagram}
                  onChange={(e) => handleChange('instagram', e.target.value)}
                  placeholder="https://instagram.com/..."
                />
                <Input
                  label="Twitter / X"
                  value={formData.twitter}
                  onChange={(e) => handleChange('twitter', e.target.value)}
                  placeholder="https://twitter.com/..."
                />
              </div>
            </Card>
          )}

          {activeSection === 'apps' && (
            <Card padding="lg">
              <CardHeader title="Mobile Apps" description="Links to your mobile applications" />
              <div className="space-y-4">
                <Input
                  label="iOS App Store URL"
                  value={formData.iosAppUrl}
                  onChange={(e) => handleChange('iosAppUrl', e.target.value)}
                  placeholder="https://apps.apple.com/..."
                />
                <Input
                  label="Google Play Store URL"
                  value={formData.androidAppUrl}
                  onChange={(e) => handleChange('androidAppUrl', e.target.value)}
                  placeholder="https://play.google.com/..."
                />
              </div>
            </Card>
          )}

          {activeSection === 'seo' && (
            <Card padding="lg">
              <CardHeader title="SEO Settings" description="Search engine optimization" />
              <div className="space-y-4">
                <Input
                  label="Default Meta Title"
                  value={formData.metaTitle}
                  onChange={(e) => handleChange('metaTitle', e.target.value)}
                  helper="60 characters max recommended"
                />
                <Textarea
                  label="Default Meta Description"
                  value={formData.metaDescription}
                  onChange={(e) => handleChange('metaDescription', e.target.value)}
                  rows={3}
                  helper="160 characters max recommended"
                />
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
